import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'framer-motion';
import { DateTime } from 'luxon';
import { FC, useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import Button from '@/components/Button';
import FancySwitch from '@/components/FancySwitch';
import { SrOnly } from '@/components/Layout/Parts';
import ScrollableTable from '@/components/ScrollableTable';
import { Table, TableContent, Th } from '@/components/Table';
import Text from '@/components/Text';
import ViewOptionsModal from '@/components/ViewOptionsModal';
import useNetwork from '@/hooks/dq/useNetwork';
import useQueueSettings from '@/hooks/dq/useSettings';
import useAlerts from '@/hooks/useAlerts';
import useTableColumnViewOption from '@/hooks/useTableColumnViewOption';
import {
  EditorialFacebookPostResource,
  EditorialQueuePage,
} from '@/types/resources';
import { api } from '@/utils/api';
import { chunk } from '@/utils/array';
// import { getLastDistributedPostKey } from '@/utils/dq';
import { EditorialFacebookPostType } from '@/utils/enums';

import BulkSelectionModal from './BulkSelectionModal';
import ContentTypeTableFilter from './ContentTypeTableFilter';
import MoreActions from './MoreActions';
import PageGuideButton from './PageGuideButton';
import PostsStatusNav from './PostsStatusNav';
import SortableTable from './SortableTable';
import TableRow from './TableRow';

const Header = styled.div`
  display: flex;
  margin: 16px 0 24px;
  align-items: flex-end;
`;

const Heading = styled(Text)`
  display: flex;
  align-items: center;
  text-transform: uppercase;
  margin-bottom: 12px;
`;

const Badge = styled(Text)`
  display: flex;
  align-items: center;
  height: 18px;
  padding: 0 9px;
  border-radius: 9px;
  background: ${({ theme }) => theme.colors.gray};
  margin-right: 12px;
`;

const StyledTable = styled(Table)`
  th,
  td {
    padding: 16px 12px;
    font-size: 11px;
    font-weight: 700;
    min-width: 140px;
    vertical-align: top;
    &:first-child {
      width: 90px;
      min-width: 90px;
      padding-left: 24px;
      position: sticky;
      z-index: 1;
      background-color: ${({ theme }) => theme.colors.white};
      left: 0;
      top: 0;
      bottom: 0;
      border-right: 1px solid ${({ theme }) => theme.colors.grayscale} !important;
    }
    &:last-child {
      min-width: 56px;
      width: 56px;
      z-index: 1;
      position: relative;
    }
  }

  th {
    vertical-align: middle;
  }
`;

const MessageTh = styled(Th)`
  min-width: 256px !important;
`;

const StyledFancySwitch = styled(FancySwitch)`
  background: ${({ theme }) => theme.colors.white};
  height: 32px;
  border-color: ${({ theme }) => theme.colors.grayscale};

  &:hover {
    border-color: ${({ theme }) => theme.colors.lightGray};
  }
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-end;
`;

const StyledViewOptionsModal = styled(ViewOptionsModal)`
  margin: 0 !important;
`;

export const CheckboxHolder = styled.div`
  position: relative;
`;

export const CheckboxLabel = styled.label`
  display: block;
  height: 20px;
  width: 20px;
  right: 10px;
  border: 2px solid ${({ theme }) => theme.colors.gray};
  background-color: ${({ theme }) => theme.colors.white};
  cursor: pointer;
  transition: border-color ease-out 0.2s;
  &:hover {
    border-color: ${({ theme }) => theme.colors.purpleLighter};
  }
`;

export const Checkbox = styled.input`
  display: none;
  &:checked + ${CheckboxLabel} {
    background: ${({ theme }) => theme.colors.purple};
    border-color: transparent;
    &:after {
      content: '';
      position: absolute;
      left: 7px;
      top: 3px;
      width: 6px;
      height: 12px;
      border: solid ${({ theme }) => theme.colors.white};
      border-width: 0 2px 2px 0;
      transform: rotate(45deg);
    }
  }
`;

export const CheckboxWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: space-between;
`;

const BulkActionsHolder = styled(motion.div)`
  position: fixed;
  bottom: 24px;
  right: 120px;
  z-index: 2;
`;

const BulkActionButton = styled(Button)`
  padding: 0 24px;
  box-shadow:
    0px 10px 30px rgba(34, 34, 34, 0.12),
    0px 20px 100px rgba(34, 34, 34, 0.06);
  min-width: 284px;
  background: ${({ theme }) => theme.colors.error};
  color: ${({ theme }) => theme.colors.white};
  border-color: ${({ theme }) => theme.colors.error};
  &:hover {
    background: #f86b54;
    color: ${({ theme }) => theme.colors.white};
    border-color: #f86b54;
  }
`;

export const tableColumns = [
  {
    title: 'Cover',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Message',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Network',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Page Name',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Content Type',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Estimated Post Date',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Status',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Site Visit Benchmark',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Reach Duration',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Article Link',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Owner',
    toggable: true,
    defaultVisibility: true,
  },
  {
    title: 'Notes',
    toggable: true,
    defaultVisibility: true,
  },
];

interface Props {
  search: string;
  activePage: EditorialQueuePage;
}

const PostsTable: FC<Props> = ({ search, activePage }) => {
  const [sortModeEnalbed, setSortModeEnabled] = useState(false);

  const [selectedRows, setSelectedRows] = useState<Set<number>>(
    () => new Set(),
  );

  const [showBulkSelectionModal, setShowBulkSelectionModal] = useState(false);

  const alerts = useAlerts();

  const [network, networkLabel] = useNetwork();

  const queryClient = useQueryClient();

  const [contentTypeFilter, setContentTypeFilter] =
    useState<EditorialFacebookPostType | null>(null);

  const { status } = useParams();

  const settings = useQueueSettings(activePage);

  const tableViewOptionsKey = `${status!}_${networkLabel}_dq_posts_table`;

  const getColumnVisibility = useTableColumnViewOption(
    tableViewOptionsKey,
    tableColumns,
  );

  const { data: lastDistributedPostData } = useQuery(
    [
      'editorial_distribution_queue_last_distributed_post',
      {
        network,
        channelId: activePage.id,
      },
    ],
    () =>
      api.get<never, { post: EditorialFacebookPostResource | null }>(
        '/v1/editorial/queue/last-distributed-post',
        {
          params: {
            network,
            channel_id: activePage.id,
          },
        },
      ),
    {
      refetchInterval: 20 * 1000,
    },
  );

  const queryKey = [
    'editorial_distribution_schedules',
    {
      network,
      queueable: true,
      status: 'queued',
      selectedChannels: activePage.id.toString(),
      search,
      contentTypeFilter,
    },
  ];

  const {
    data: posts,
    isLoading,
    isError,
  } = useQuery(
    queryKey,
    () =>
      api.get<never, EditorialFacebookPostResource[]>(
        '/v1/editorial/facebook-posts',
        {
          params: {
            'filter[network]': network,
            'filter[queueStatus]': '1',
            'filter[channelId]': activePage.id,
            sort: 'queueOrder',
            withoutPagination: 1,
            ...(!!search && {
              'filter[search]': search,
            }),
            ...(!!contentTypeFilter && {
              'filter[type]': contentTypeFilter,
            }),
          },
        },
      ),
    {
      refetchInterval: 20 * 1000,
    },
  );

  const toggleSelection = useCallback((id: number) => {
    setSelectedRows((prev) => {
      const newSet = new Set(prev);
      newSet.has(id) ? newSet.delete(id) : newSet.add(id);
      return newSet;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    if (selectedRows.size) {
      return setSelectedRows(new Set());
    }

    setShowBulkSelectionModal(true);
  }, [selectedRows]);

  const onBulkDelete = async () => {
    try {
      const confirmed = await alerts.confirm(
        'Are you sure?',
        `Delete ${selectedRows.size} pending post${selectedRows.size === 1 ? '' : 's'}`,
      );

      if (!confirmed) {
        return;
      }

      const batches = chunk(Array.from(selectedRows), 50);

      for (const batch of batches) {
        await api.delete(
          `/v1/editorial/queue/posts?ids=${batch.join(',')}&channel_id=${activePage.id}&network=${network}`,
        );
      }

      await queryClient.invalidateQueries(queryKey);

      setSelectedRows(new Set());

      alerts.success('Selected pending posts deleted successfully');
    } catch {
      alerts.error();
    }
  };

  const calculatePostDate = useCallback(
    (index: number) => {
      if (!settings?.enabled) {
        return null;
      }

      const lastDistributedPost = lastDistributedPostData?.post;

      const start = DateTime.fromISO(settings.startTime!);

      if (!lastDistributedPost) {
        return start.plus({ minutes: settings.frequency * index });
      }

      const lastPostDate = DateTime.fromISO(lastDistributedPost.publishedAt);

      if (lastPostDate < start) {
        const diffInMinutes = Math.ceil(
          start.diff(lastPostDate, 'minutes').minutes,
        );

        if (diffInMinutes < settings.frequency) {
          return lastPostDate.plus({
            minutes: (index + 1) * settings.frequency,
          });
        }

        return start.plus({ minutes: settings.frequency * index });
      }

      return lastPostDate.plus({
        minutes: settings.frequency * (index + 1),
      });
    },
    [settings, lastDistributedPostData],
  );

  const estimatedEndTime = useMemo(() => {
    if (!settings?.enabled) {
      return null;
    }

    if (!posts?.length) {
      return null;
    }

    const lastDistributedPost = lastDistributedPostData?.post;

    let start = DateTime.fromISO(settings.startTime!);

    if (lastDistributedPost) {
      const publishedAt = DateTime.fromISO(lastDistributedPost.publishedAt);

      if (publishedAt > start) {
        return publishedAt.plus({
          minutes: (posts.length - 1) * settings.frequency,
        });
      }

      const diffInMinutes = Math.ceil(
        start.diff(publishedAt, 'minutes').minutes,
      );

      if (diffInMinutes < settings.frequency) {
        return publishedAt.plus({
          minutes: posts.length * settings.frequency,
        });
      }
    }

    return start.plus({
      minutes: (posts.length - 1) * settings.frequency,
    });
  }, [posts?.length, settings, lastDistributedPostData]);

  return (
    <>
      <Header>
        <PostsStatusNav />
        <Actions>
          <StyledFancySwitch
            text="Reorder:"
            isActive={sortModeEnalbed}
            onClick={() => setSortModeEnabled((prev) => !prev)}
          />
          <ContentTypeTableFilter
            contentType={contentTypeFilter}
            setContentType={setContentTypeFilter}
          />
          <StyledViewOptionsModal
            columns={tableColumns}
            tableKey={tableViewOptionsKey}
          />
          <PageGuideButton activePage={activePage} />
          <MoreActions activePage={activePage} />
        </Actions>
      </Header>
      <Heading $color="gray" $type="smallHeadline" as="div">
        <Badge as="div" $type="extraSmall" $color="white">
          {posts?.length ?? 0}
        </Badge>
        {activePage.name}
        {!!settings?.frequency && ` · every ${settings.frequency} minutes`}
        {!!estimatedEndTime &&
          ` · Estimated end time · ${estimatedEndTime?.toFormat('yy/LL/dd HH:mm') ?? ''}`}
      </Heading>
      {sortModeEnalbed ? (
        <SortableTable posts={posts ?? []} />
      ) : (
        <ScrollableTable>
          <StyledTable $stickyActions>
            <thead>
              <tr>
                <Th>
                  <CheckboxWrap>
                    <CheckboxHolder>
                      <Checkbox
                        id={`checkbox-all-rows`}
                        name="products"
                        type="checkbox"
                        disabled={!posts?.length}
                        checked={
                          !!selectedRows.size &&
                          selectedRows.size === posts?.length
                        }
                        onChange={toggleSelectAll}
                      />
                      <CheckboxLabel htmlFor={`checkbox-all-rows`} />
                    </CheckboxHolder>
                    Order Nr.
                  </CheckboxWrap>
                </Th>
                {getColumnVisibility('Cover') && <Th>Cover</Th>}
                {getColumnVisibility('Message') && (
                  <MessageTh>Message</MessageTh>
                )}
                {getColumnVisibility('Network') && <Th>Network</Th>}
                {getColumnVisibility('Page Name') && <Th>Page Name</Th>}
                {getColumnVisibility('Content Type') && <Th>Content Type</Th>}
                {getColumnVisibility('Estimated Post Date') && (
                  <Th>Estimated Post Date</Th>
                )}
                {getColumnVisibility('Status') && <Th>Status</Th>}
                {getColumnVisibility('Site Visit Benchmark') && (
                  <Th>Site Visit Benchmark</Th>
                )}
                {getColumnVisibility('Reach Duration') && (
                  <Th>Reach Duration</Th>
                )}
                {getColumnVisibility('Article Link') && <Th>Article Link</Th>}
                {getColumnVisibility('Owner') && <Th>Owner</Th>}
                {getColumnVisibility('Notes') && <Th>Notes</Th>}
                <Th $actionsCol>
                  <SrOnly>Actions</SrOnly>
                </Th>
              </tr>
            </thead>
            <tbody>
              <TableContent
                isLoading={isLoading}
                isError={isError}
                hasData={!!posts?.length}
              >
                {posts?.map((p, i) => (
                  <TableRow
                    post={p}
                    key={p.id}
                    order={i + 1}
                    postDate={calculatePostDate(i)}
                    activePage={activePage}
                    toggleSelection={toggleSelection}
                    isChecked={selectedRows.has(p.id)}
                  />
                ))}
              </TableContent>
            </tbody>
          </StyledTable>
        </ScrollableTable>
      )}
      <AnimatePresence>
        {!!selectedRows.size && (
          <BulkActionsHolder
            initial={{ scale: 0, opacity: 0.3 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0.3 }}
          >
            <BulkActionButton onClick={onBulkDelete}>
              Delete {selectedRows.size} selected post
              {selectedRows.size === 1 ? '' : 's'}
            </BulkActionButton>
          </BulkActionsHolder>
        )}
        {showBulkSelectionModal && (
          <BulkSelectionModal
            posts={posts ?? []}
            setSelectedRows={setSelectedRows}
            onClose={() => setShowBulkSelectionModal(false)}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default PostsTable;
